/* ==========================================================================
   Ecoline — wishlist.js (spec §5)
   LOCAL-ONLY wishlist: an array of product handles in localStorage under the
   key `ecoline_wishlist`. There is no backend — the list lives in this
   browser only (the header label says "saved on this device").

   Contracts (used by the header + product cards):
     - [data-wishlist-toggle="<product-handle>"] — click toggles that handle;
       the element gets .is-active + aria-pressed while saved. Delegated, so
       cards re-rendered by PLP filtering keep working. The handle may live
       on a separate data-wishlist-handle attribute instead (product-card
       emits both) — handleOf() below accepts either.
     - [data-wishlist-count] — badge: textContent = count, .is-empty at 0.
     - [data-wishlist-header] — the header heart; its aria-label is kept in
       sync with the count.
     - `ecoline:wishlist-updated` on document, detail { handles, count } —
       fired after every mutation (the header listens via renderAll below).
     - window.Ecoline.wishlist = { list, count, has, add, remove, toggle }.

   Respects the `wishlist_enabled` theme setting via
   window.EcolineSettings.wishlistEnabled. Vanilla, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'ecoline_wishlist';

  function enabled() {
    var s = window.EcolineSettings || {};
    return s.wishlistEnabled !== false;
  }

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];
      return arr.filter(function (h) { return typeof h === 'string' && h.length > 0; });
    } catch (e) {
      return [];
    }
  }

  function write(handles) {
    try { localStorage.setItem(KEY, JSON.stringify(handles)); } catch (e) { /* noop — private mode */ }
  }

  function dispatch(handles) {
    document.dispatchEvent(new CustomEvent('ecoline:wishlist-updated', {
      detail: { handles: handles.slice(), count: handles.length }
    }));
  }

  var wishlist = {
    list: function () { return read(); },
    count: function () { return read().length; },
    has: function (handle) { return read().indexOf(handle) !== -1; },
    add: function (handle) {
      if (!handle) return false;
      var handles = read();
      if (handles.indexOf(handle) === -1) {
        handles.push(handle);
        write(handles);
        dispatch(handles);
      }
      return true;
    },
    remove: function (handle) {
      var handles = read();
      var i = handles.indexOf(handle);
      if (i !== -1) {
        handles.splice(i, 1);
        write(handles);
        dispatch(handles);
      }
      return false;
    },
    toggle: function (handle) {
      if (this.has(handle)) return this.remove(handle);
      return this.add(handle);
    }
  };

  /* Expose on the shared global (app.js loads first and creates it). */
  window.Ecoline = window.Ecoline || {};
  window.Ecoline.wishlist = wishlist;

  /* ---- DOM sync ---- */

  /* The product handle for a toggle element: prefer data-wishlist-handle,
     fall back to the value of data-wishlist-toggle itself. */
  function handleOf(el) {
    return el.getAttribute('data-wishlist-handle') || el.getAttribute('data-wishlist-toggle') || '';
  }

  function renderAll() {
    var handles = read();
    var count = handles.length;

    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });

    document.querySelectorAll('[data-wishlist-header]').forEach(function (el) {
      el.setAttribute(
        'aria-label',
        count === 0
          ? 'Wishlist — nothing saved on this device yet'
          : 'Wishlist — ' + count + ' saved on this device'
      );
    });

    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (el) {
      var on = handles.indexOf(handleOf(el)) !== -1;
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function init() {
    if (!enabled()) return;

    renderAll();

    /* Delegated heart toggles — survives PLP filter re-renders. */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-wishlist-toggle]') : null;
      if (!btn) return;
      e.preventDefault();
      wishlist.toggle(handleOf(btn));
    });

    document.addEventListener('ecoline:wishlist-updated', renderAll);

    /* Cross-tab sync. */
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) renderAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
